import { Folder, Note, UserSettings, isUserSettings } from "../../shared/types";
import { modelVersion } from "../utils/environment";
import indexById from "../utils/indexById";
import { RohanCache } from "./rohanCache";
import { swLogger } from "./swLogger";

const logger = swLogger.with({ namespace: "sw/cache" });

export enum CacheKeys {
  notes = "notes",
  folders = "folders",
  notesDirtySet = "notes-dirtySet",
  foldersDirtySet = "folders-dirtySet",
  metadata = "metadata",
  user = "user",
}

const cache = new RohanCache(
  [
    CacheKeys.notes,
    CacheKeys.folders,
    CacheKeys.notesDirtySet,
    CacheKeys.foldersDirtySet,
    CacheKeys.user,
    CacheKeys.metadata,
  ],
  modelVersion,
);

type RohanDelta = {
  [CacheKeys.notes]?: Record<string, Note>;
  [CacheKeys.folders]?: Record<string, Folder>;
  [CacheKeys.notesDirtySet]?: { upserts: string[] };
  [CacheKeys.foldersDirtySet]?: { upserts: string[] };
  [CacheKeys.user]?: { userSettings?: UserSettings; isDirty: boolean };
  [CacheKeys.metadata]?: { checkpoint: number };
};

type CacheData = {
  [CacheKeys.notes]?: Note[];
  [CacheKeys.folders]?: Folder[];
  [CacheKeys.notesDirtySet]?: string[];
  [CacheKeys.foldersDirtySet]?: string[];
  [CacheKeys.user]?: { userSettings?: UserSettings; isDirty: boolean };
  [CacheKeys.metadata]?: { checkpoint: number };
};

export function upsertToCache(data: CacheData): Promise<void> {
  const delta: RohanDelta = {};
  if (data[CacheKeys.notes]) delta[CacheKeys.notes] = indexById(data[CacheKeys.notes]);
  if (data[CacheKeys.folders]) delta[CacheKeys.folders] = indexById(data[CacheKeys.folders]);
  if (data[CacheKeys.notesDirtySet]) delta[CacheKeys.notesDirtySet] = { upserts: data[CacheKeys.notesDirtySet] };
  if (data[CacheKeys.foldersDirtySet]) delta[CacheKeys.foldersDirtySet] = { upserts: data[CacheKeys.foldersDirtySet] };
  if (data[CacheKeys.user]) delta[CacheKeys.user] = data[CacheKeys.user];
  if (data[CacheKeys.metadata]) delta[CacheKeys.metadata] = data[CacheKeys.metadata];
  if (Object.keys(delta).length === 0) return Promise.resolve();
  return cache.saveDelta(delta);
}

export function clearCacheKey(key: CacheKeys) {
  return cache.clearTable(key);
}

export async function clearCache() {
  await cache.clear();
}

export function vacuumCache() {
  return cache.vacuum();
}

/**
 * This just assumes that the cache is valid, and returns the data.
 */
export async function readCache() {
  console.log("[debug] inside read");
  const validCachedData: CacheData = {};
  const cachedData = await cache.load();
  if (typeof cachedData !== "object" || cachedData === null) {
    console.log("[debug] cache data is not an object");
    logger.error("Cache data is not an object", { context: { cachedData } });
    return validCachedData;
  }
  validCachedData.notes = typeof cachedData?.notes?.data === "object" ? Object.values(cachedData.notes.data) : [];
  validCachedData.folders = typeof cachedData?.folders?.data === "object" ? Object.values(cachedData.folders.data) : [];
  validCachedData[CacheKeys.notesDirtySet] = cachedData[CacheKeys.notesDirtySet]?.data?.upserts ?? [];
  validCachedData[CacheKeys.foldersDirtySet] = cachedData[CacheKeys.foldersDirtySet]?.data?.upserts ?? [];
  const userData = cachedData[CacheKeys.user]?.data;
  validCachedData[CacheKeys.user] = {
    userSettings: isUserSettings(userData?.userSettings) ? userData.userSettings : undefined,
    isDirty: userData?.isDirty || false,
  };
  console.log("[debug] Note stuff", typeof cachedData?.notes?.data === "object", validCachedData.notes.length);
  const metadata = cachedData[CacheKeys.metadata]?.data;
  validCachedData[CacheKeys.metadata] = { checkpoint: metadata?.checkpoint ?? 0 };
  console.log("[debug] metadata stuff", cachedData[CacheKeys.metadata], validCachedData[CacheKeys.metadata]);
  return validCachedData;
}
